import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, DollarSign, AlertCircle, Play, Edit3, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to get Brazil local date (UTC-3)
const getBrazilDate = () => {
  const now = new Date();
  // Convert to Brazil timezone (UTC-3)
  const brazilTime = new Date(now.getTime() - (3 * 60 * 60 * 1000));
  return brazilTime.toISOString().split('T')[0];
};

const PendingBets = () => {
  const { user } = useAuth();
  const [pendingBets, setPendingBets] = useState([]);
  const [bettingHouses, setBettingHouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completingBet, setCompletingBet] = useState(null);
  const [editingBet, setEditingBet] = useState(null);
  const [completeFormData, setCompleteFormData] = useState({
    result: 'green',
    cashout_value: '',
    comments: ''
  });
  const [editFormData, setEditFormData] = useState({
    betting_house_id: '',
    group_id: '',
    match_description: '',
    game_datetime: '',
    odd: '',
    stake: '',
    comments: ''
  });

  useEffect(() => {
    if (user) {
      loadPendingBets();
      loadSupportData();
    }
  }, [user]);

  const loadPendingBets = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets/pending?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setPendingBets(data);
      }
    } catch (error) {
      console.error('Error loading pending bets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSupportData = async () => {
    try {
      // Load betting houses
      const housesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`);
      if (housesResponse.ok) {
        const housesData = await housesResponse.json();
        setBettingHouses(housesData);
      }

      // Load groups
      const groupsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`);
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }
    } catch (error) {
      console.error('Error loading support data:', error);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit'
    })}`;
  };

  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getBettingHouseName = (houseId) => {
    const house = bettingHouses.find(h => h.id === houseId);
    return house ? house.name : 'N/A';
  };

  const getGroupName = (groupId) => {
    if (!groupId) return 'Sem Grupo';
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'N/A';
  };

  const getTimeUntilGame = (gameDateTime) => {
    if (!gameDateTime) return null;
    
    const now = new Date();
    const gameDate = new Date(gameDateTime);
    const diffMs = gameDate - now;
    
    if (diffMs < 0) {
      const pastHours = Math.abs(Math.floor(diffMs / (1000 * 60 * 60)));
      return { text: `Há ${pastHours}h atrás`, isPast: true, isUrgent: pastHours > 24 };
    }
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffDays > 0) {
      return { text: `Em ${diffDays}d ${diffHours}h`, isPast: false, isUrgent: false };
    } else if (diffHours > 0) {
      return { text: `Em ${diffHours}h ${diffMinutes}m`, isPast: false, isUrgent: diffHours < 2 };
    } else {
      return { text: `Em ${diffMinutes}m`, isPast: false, isUrgent: true };
    }
  };

  const handleCompleteStart = (bet) => {
    setCompletingBet(bet);
    setCompleteFormData({
      result: 'green',
      cashout_value: '',
      comments: bet.comments || ''
    });
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        result: completeFormData.result,
        comments: completeFormData.comments
      };

      if (completeFormData.result === 'cashout' && completeFormData.cashout_value) {
        payload.cashout_value = parseFloat(completeFormData.cashout_value);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets/${completingBet.id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await loadPendingBets(); // Reload pending bets
        setCompletingBet(null);
        alert('✅ Aposta finalizada com sucesso!');
      } else {
        alert('❌ Erro ao finalizar aposta. Tente novamente.');
      }
    } catch (error) {
      console.error('Error completing bet:', error);
      alert('❌ Erro ao finalizar aposta. Tente novamente.');
    }
  };

  const handleEditStart = (bet) => {
    setEditingBet(bet);
    setEditFormData({
      betting_house_id: bet.betting_house_id,
      group_id: bet.group_id,
      match_description: bet.match_description || '',
      game_datetime: bet.game_datetime ? new Date(bet.game_datetime).toISOString().split('T')[0] : '',
      odd: bet.odd,
      stake: bet.stake,
      comments: bet.comments || ''
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        betting_house_id: editFormData.betting_house_id,
        group_id: editFormData.group_id,
        match_description: editFormData.match_description,
        game_datetime: editFormData.game_datetime ? `${editFormData.game_datetime}T12:00:00` : null,
        odd: parseFloat(editFormData.odd),
        stake: parseFloat(editFormData.stake),
        comments: editFormData.comments
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets/${editingBet.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await loadPendingBets(); // Reload pending bets
        setEditingBet(null);
        alert('✅ Aposta editada com sucesso!');
      } else {
        alert('❌ Erro ao editar aposta. Tente novamente.');
      }
    } catch (error) {
      console.error('Error editing bet:', error);
      alert('❌ Erro ao editar aposta. Tente novamente.');
    }
  };

  const handleDelete = async (bet) => {
    console.log('🗑️ handleDelete chamado para bet:', bet);
    
    if (!confirm(`Tem certeza que deseja excluir a aposta "${bet.match_description}"?`)) {
      console.log('❌ Usuário cancelou a exclusão');
      return;
    }
    
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/bets/${bet.id}`;
      console.log('🔗 URL de exclusão:', url);
      console.log('📝 Bet ID:', bet.id);
      
      const response = await fetch(url, {
        method: 'DELETE'
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Resposta do servidor:', data);
        await loadPendingBets(); // Reload pending bets
        alert('✅ Aposta excluída com sucesso!');
      } else {
        const errorText = await response.text();
        console.error('❌ Erro na resposta:', response.status, errorText);
        alert('❌ Erro ao excluir aposta. Tente novamente.');
      }
    } catch (error) {
      console.error('💥 Error deleting bet:', error);
      alert('❌ Erro ao excluir aposta. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-500 ml-4">Carregando apostas pendentes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Clock className="w-8 h-8 mr-3 text-orange-600" />
            Apostas Pendentes
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Finalize o resultado das suas apostas em andamento
          </p>
        </div>
        <div className="bg-orange-100 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-4 py-2">
          <span className="text-orange-800 dark:text-orange-200 font-medium">
            {pendingBets.length} apostas pendentes
          </span>
        </div>
      </div>

      {/* Pending Bets List */}
      {pendingBets.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nenhuma aposta pendente
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Todas as suas apostas foram finalizadas
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingBets.map((bet) => {
            return (
              <div 
                key={bet.id} 
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header with status */}
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Pendente
                      </span>
                      )}
                    </div>

                    {/* Match description */}
                    {bet.match_description && (
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {bet.match_description}
                      </h3>
                    )}

                    {/* Bet details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Casa de Apostas</p>
                        <p className="font-medium text-gray-900 dark:text-white">{getBettingHouseName(bet.betting_house_id)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Grupo</p>
                        <p className="font-medium text-gray-900 dark:text-white">{getGroupName(bet.group_id)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Tipo</p>
                        <p className="font-medium text-gray-900 dark:text-white">{bet.bet_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Odd</p>
                        <p className="font-medium text-gray-900 dark:text-white">{bet.odd}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Stake</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(bet.stake)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Registrada em</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{formatDateTime(bet.date)}</p>
                      </div>
                    </div>

                    {/* Comments */}
                    {bet.comments && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Comentários:</strong> {bet.comments}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="ml-6 flex flex-col space-y-2">
                    <button
                      onClick={() => handleCompleteStart(bet)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span>Finalizar</span>
                    </button>
                    
                    <button
                      onClick={() => handleEditStart(bet)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Editar</span>
                    </button>
                    
                    <button
                      onClick={() => handleDelete(bet)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Excluir</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Complete Bet Modal */}
      {completingBet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
              Finalizar Aposta
            </h3>
            
            {/* Bet summary */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <p className="font-medium text-gray-900 dark:text-white">
                {completingBet.match_description || 'Aposta'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Stake: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(completingBet.stake)} • Odd: {completingBet.odd}
              </p>
            </div>

            <form onSubmit={handleCompleteSubmit} className="space-y-4">
              {/* Result */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Resultado *
                </label>
                <select
                  value={completeFormData.result}
                  onChange={(e) => setCompleteFormData(prev => ({ ...prev, result: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="green">✅ Green (Vitória)</option>
                  <option value="red">❌ Red (Derrota)</option>
                  <option value="void">⚪ Void (Anulada)</option>
                  <option value="cashout">💰 Cashout</option>
                </select>
              </div>

              {/* Cashout value */}
              {completeFormData.result === 'cashout' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor do Cashout
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={completeFormData.cashout_value}
                    onChange={(e) => setCompleteFormData(prev => ({ ...prev, cashout_value: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              )}

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comentários
                </label>
                <textarea
                  value={completeFormData.comments}
                  onChange={(e) => setCompleteFormData(prev => ({ ...prev, comments: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Observações sobre o resultado..."
                />
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setCompletingBet(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Finalizar Aposta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Bet Modal */}
      {editingBet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Edit3 className="w-5 h-5 mr-2 text-blue-600" />
              Editar Aposta Pendente
            </h3>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Casa de Apostas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Casa de Apostas *
                </label>
                <select
                  value={editFormData.betting_house_id}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, betting_house_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Selecione uma casa de apostas</option>
                  {bettingHouses.map(house => (
                    <option key={house.id} value={house.id}>{house.name}</option>
                  ))}
                </select>
              </div>

              {/* Grupo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Grupo *
                </label>
                <select
                  value={editFormData.group_id}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, group_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Selecione um grupo</option>
                  {groups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>

              {/* Descrição do Jogo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição do Jogo *
                </label>
                <input
                  type="text"
                  value={editFormData.match_description}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, match_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: Real Madrid vs Barcelona, Over 2.5 Goals"
                  required
                />
              </div>

              {/* Data do Jogo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data do Jogo *
                </label>
                <input
                  type="date"
                  value={editFormData.game_datetime}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, game_datetime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min={getBrazilDate()}
                  required
                />
              </div>

              {/* Odd e Stake */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Odd *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormData.odd}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, odd: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="1.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Stake (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormData.stake}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, stake: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="0.01"
                    required
                  />
                </div>
              </div>

              {/* Comentários */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comentários
                </label>
                <textarea
                  value={editFormData.comments}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, comments: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Observações adicionais..."
                />
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingBet(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingBets;